import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  camerasAPI,
  isInsufficientFundsError,
  modelsAPI,
  notifyNotificationsChanged,
} from '../../utils/api';
import {
  // cameraHasPeopleCounterModel,
  isPeopleCounterModel,
} from '../../utils/peopleCounter';
import { getModelDisplayName } from '../../utils/modelLabels';
import {
  cameraHasPresenceCheckModel,
  DEFAULT_PRESENCE_SETTINGS,
  isPresenceCheckModel,
} from '../../utils/presence';
import '../../pages/Area/Area.css';

interface AreaOption {
  id: number;
  name: string;
}

interface AddCameraModalProps {
  areas: AreaOption[];
  onClose: () => void;
  onSuccess?: () => void;
  defaultAreaId?: number | null;
}

const modalIconsBase = `${process.env.PUBLIC_URL}/icons/modals`;

function getModelModalIcon(model: string): string {
  const modelKey = (model || '').trim().toLowerCase();

  if (modelKey.includes('license_plate') || modelKey.includes('plate')) {
    return 'lock.svg';
  }
  if (modelKey === 'fire_and_smoke_detector' || modelKey === 'fire') {
    return 'fire-smoke.svg';
  }
  if (modelKey === 'smoke') {
    return 'smoke.svg';
  }
  if (
    modelKey.includes('multiple_rtsp') ||
    ['car', 'vehicle', 'bus', 'truck', 'police', 'ambulance', 'firetruck', 'emergency_car_detector'].includes(
      modelKey
    )
  ) {
    return 'car.svg';
  }
  if (modelKey === 'people' || modelKey === 'person' || modelKey === 'utrau_staff_detector') {
    return 'people.svg';
  }
  if (
    modelKey === 'smoking' ||
    modelKey === 'smoking_detector' ||
    modelKey === 'smoking_detector_v2' ||
    modelKey === 'cigarette'
  ) {
    return 'kyrenie.svg';
  }
  if (modelKey === 'garbage_truck_detector' || modelKey === 'garbage_truck') {
    return 'mysor.svg';
  }
  if (modelKey === 'tractor_detector' || modelKey === 'tractor') {
    return 'tractor.svg';
  }

  return 'lock.svg';
}

const AddCameraModal: React.FC<AddCameraModalProps> = ({
  areas,
  onClose,
  onSuccess,
  defaultAreaId = null,
}) => {
  const [areaId, setAreaId] = useState<number | ''>('');
  const [name, setName] = useState('');
  const [rtsp, setRtsp] = useState('');
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [peopleCounterEnabled, setPeopleCounterEnabled] = useState(false);
  const [presenceCheckEnabled, setPresenceCheckEnabled] = useState(false);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [modelTitles, setModelTitles] = useState<Record<string, string>>({});
  const [modelsLoading, setModelsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  /** Оплата камеры: автопродление с баланса и нехватка средств. */
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [autoRenew, setAutoRenew] = useState(false);
  const [fundsError, setFundsError] = useState(false);
  const cameraMonthlyPrice = user?.camera_monthly_price ?? 1000;
  const userBalance = user?.balance ?? 0;
  const canAffordCamera = userBalance >= cameraMonthlyPrice;

  const selectedHasPeopleModel = false; // счётчик временно отключён
  // useMemo(() => cameraHasPeopleCounterModel(selectedModels), [selectedModels]);

  const selectedHasPresenceModel = useMemo(
    () => cameraHasPresenceCheckModel(selectedModels),
    [selectedModels]
  );

  useEffect(() => {
    if (areas.length === 0) {
      setAreaId('');
      return;
    }
    const preferred =
      defaultAreaId != null && areas.some((a) => a.id === defaultAreaId)
        ? defaultAreaId
        : areas[0].id;
    setAreaId(preferred);
  }, [areas, defaultAreaId]);

  useEffect(() => {
    let cancelled = false;

    const loadModels = async () => {
      setModelsLoading(true);
      try {
        const response = await modelsAPI.getAll();
        if (!cancelled && response.success) {
          setAvailableModels((response.models || []).map((model) => model.name));
          setModelTitles(Object.fromEntries((response.models || []).map((model) => [model.name, model.title])));
        }
      } catch {
        if (!cancelled) setAvailableModels([]);
      } finally {
        if (!cancelled) setModelsLoading(false);
      }
    };

    loadModels();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggleModel = (modelName: string) => {
    setSelectedModels((prev) => {
      const next = prev.includes(modelName)
        ? prev.filter((m) => m !== modelName)
        : [...prev, modelName];
      if (!next.some(isPeopleCounterModel)) {
        setPeopleCounterEnabled(false);
      }
      if (!next.some(isPresenceCheckModel)) {
        setPresenceCheckEnabled(false);
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!areaId || !name.trim() || !rtsp.trim()) {
      setError('Заполните все обязательные поля');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const response = await camerasAPI.create(
        name.trim(),
        rtsp.trim(),
        Number(areaId),
        selectedModels,
        autoRenew
      );

      // С баланса списана оплата за первый месяц
      void refreshUser();

      if (!response.success) {
        setError(response.message || 'Ошибка добавления камеры');
        return;
      }

      if (response.queued) {
        notifyNotificationsChanged();
        onSuccess?.();
        onClose();
        return;
      }

      const extId = response.camera?.camera_id;
      // Счётчик людей временно отключён
      // if (extId && cameraHasPeopleCounterModel(selectedModels) && peopleCounterEnabled) {
      //   const pc = await camerasAPI.enablePeopleCounter(extId);
      //   if (!pc.success) {
      //     setError(pc.message || 'Камера создана, но не удалось включить счётчик людей');
      //     onSuccess?.();
      //     return;
      //   }
      // }
      if (extId && cameraHasPresenceCheckModel(selectedModels) && presenceCheckEnabled) {
        const pr = await camerasAPI.setPresenceSettings(extId, {
          ...DEFAULT_PRESENCE_SETTINGS,
          enabled: true,
        });
        if (!pr.success) {
          setError(pr.message || 'Камера создана, но не удалось включить проверку присутствия');
          onSuccess?.();
          return;
        }
        if (pr.queued) notifyNotificationsChanged();
      }

      onSuccess?.();
      onClose();
    } catch (err: unknown) {
      if (isInsufficientFundsError(err)) {
        setFundsError(true);
        setError((err as any)?.response?.message || 'Недостаточно средств на балансе для оплаты камеры');
        void refreshUser();
        return;
      }
      setFundsError(false);
      const e = err as { message?: string; response?: { message?: string; rtsp_hint?: string } };
      let errorMessage = e.message || e.response?.message || 'Ошибка добавления камеры';
      if (e.response?.rtsp_hint) {
        errorMessage = `${errorMessage}\n\n${e.response.rtsp_hint}`;
      }
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="area-modal" role="dialog" aria-modal="true" aria-labelledby="add-camera-title">
      <div className="area-modal__backdrop" onClick={onClose} />
      <div className="area-modal__dialog">
        <header className="area-modal__header">
          <h2 id="add-camera-title" className="area-modal__title">
            Добавить новую камеру
          </h2>
          <button type="button" className="area-modal__close" aria-label="Закрыть" onClick={onClose}>
            ×
          </button>
        </header>

        {areas.length === 0 ? (
          <div className="area-modal__body">
            <p style={{ margin: 0, fontSize: '15px', color: '#6c6c70', lineHeight: 1.5 }}>
              Сначала создайте участок на главной странице, затем добавьте камеру.
            </p>
            <footer className="area-modal__footer">
              <button
                type="button"
                className="area-modal__submit area-modal__submit--secondary"
                onClick={onClose}
              >
                Закрыть
              </button>
            </footer>
          </div>
        ) : (
          <form className="area-modal__body" onSubmit={handleSubmit}>
            <div className="area-modal__left">
              <div className="area-modal__field">
                <input
                  className="area-modal__input"
                  type="text"
                  placeholder="Название камеры"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div className="area-modal__field">
                <input
                  className="area-modal__input"
                  type="url"
                  placeholder="RTSP URL камеры"
                  value={rtsp}
                  onChange={(e) => setRtsp(e.target.value)}
                  required
                />
              </div>
              <div className="area-modal__field">
                <select
                  className="area-modal__input area-modal__input--select"
                  value={areaId}
                  onChange={(e) => setAreaId(e.target.value ? Number(e.target.value) : '')}
                  required
                  aria-label="Участок"
                >
                  {areas.map((area) => (
                    <option key={area.id} value={area.id}>
                      {area.name}
                    </option>
                  ))}
                </select>
              </div>
              {(selectedHasPeopleModel || selectedHasPresenceModel) && (
                <div className="area-modal__people-options">
                  {/* Счётчик людей временно отключён
                  {selectedHasPeopleModel && (
                    <label className="area-modal__people-counter-option">
                      <input
                        type="checkbox"
                        checked={peopleCounterEnabled}
                        onChange={(e) => setPeopleCounterEnabled(e.target.checked)}
                      />
                      <span className="area-modal-analytic__checkbox" aria-hidden="true" />
                      <span>Включить счётчик людей</span>
                    </label>
                  )}
                  */}
                  {selectedHasPresenceModel && (
                    <label className="area-modal__people-counter-option">
                      <input
                        type="checkbox"
                        checked={presenceCheckEnabled}
                        onChange={(e) => setPresenceCheckEnabled(e.target.checked)}
                      />
                      <span className="area-modal-analytic__checkbox" aria-hidden="true" />
                      <span>Проверка присутствия</span>
                    </label>
                  )}
                </div>
              )}

              <div className="area-modal__billing">
                <div className="area-modal__billing-row">
                  <span>Стоимость камеры</span>
                  <strong>{cameraMonthlyPrice.toLocaleString('ru-RU')} ₽ / месяц</strong>
                </div>
                <div className="area-modal__billing-row">
                  <span>На балансе</span>
                  <strong>{userBalance.toLocaleString('ru-RU')} ₽</strong>
                </div>
                <p className="area-modal__billing-note">
                  Оплата за первый месяц спишется с баланса при добавлении камеры.
                </p>
                <label className="area-modal__people-counter-option">
                  <input
                    type="checkbox"
                    checked={autoRenew}
                    onChange={(e) => setAutoRenew(e.target.checked)}
                  />
                  <span className="area-modal-analytic__checkbox" aria-hidden="true" />
                  <span>Автопродление с баланса</span>
                </label>
                {!canAffordCamera && (
                  <div className="area-modal__billing-warning">
                    Недостаточно средств: нужно {cameraMonthlyPrice.toLocaleString('ru-RU')} ₽.{' '}
                    <button
                      type="button"
                      className="area-modal__billing-link"
                      onClick={() => {
                        onClose();
                        navigate('/balance');
                      }}
                    >
                      Пополнить баланс
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="area-modal__right">
              <h3 className="area-modal__subtitle">Аналитики:</h3>
              <div className="area-modal__analytics-list">
                {modelsLoading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#6c6c70' }}>
                    Загрузка моделей...
                  </div>
                ) : availableModels.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#6c6c70' }}>
                    Нет доступных моделей
                  </div>
                ) : (
                  availableModels.map((model) => (
                    <label key={model} className="area-modal-analytic">
                      <span className="area-modal-analytic__icon">
                        <img src={`${modalIconsBase}/${getModelModalIcon(model)}`} alt="" />
                      </span>
                      <span className="area-modal-analytic__text">{modelTitles[model] || getModelDisplayName(model)}</span>
                      <input
                        type="checkbox"
                        checked={selectedModels.includes(model)}
                        onChange={() => handleToggleModel(model)}
                      />
                      <span className="area-modal-analytic__checkbox" aria-hidden="true" />
                    </label>
                  ))
                )}
              </div>
            </div>

            {error && (
              <div
                style={{
                  gridColumn: '1 / -1',
                  color: '#ff4444',
                  padding: '10px',
                  marginTop: '10px',
                  fontSize: '14px',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {error}
                {fundsError && (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="area-modal__billing-link"
                      onClick={() => {
                        onClose();
                        navigate('/balance');
                      }}
                    >
                      Пополнить баланс
                    </button>
                  </>
                )}
              </div>
            )}

            <footer className="area-modal__footer">
              <button
                type="submit"
                className="area-modal__submit"
                disabled={submitting || modelsLoading || !canAffordCamera}
              >
                <span className="area-modal__submit-plus">+</span>
                <span>{submitting ? 'Добавление...' : 'Добавить камеру'}</span>
              </button>
            </footer>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddCameraModal;
